import React, { useMemo } from "react";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";
import { formatPromptCount } from "@/utils/ai/utils";
import { CoinsIcon } from "lucide-react";
import { UsageText } from "@/components/Pricing/usage-text";
import { Tooltip } from "react-tooltip";
import { useSession } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { useClaudeCodeAuthStore } from "@/store/zustand/claudeCodeAuthStore";

interface Props {}

const TopUsageIndicator: React.FC<Props> = () => {
  const { remainingUsage } = useUsageLimits();
  const { session } = useSession();
  const authStatus = useClaudeCodeAuthStore((state) => state.authStatus);
  const hasClaudeCodeAuth = authStatus === "valid";

  const totalLeft = useMemo(() => {
    if (
      remainingUsage.subscriptionTier === "unlimited" ||
      remainingUsage.subscriptionTier === "new_unlimited"
    ) {
      return "Unlimited";
    }

    if (remainingUsage.usingPrompts) {
      const basePromptsLeft =
        remainingUsage.subscriptionTier === "free"
          ? Math.min(
              remainingUsage.dailyPromptsLeft || 0,
              remainingUsage.monthlyPromptsLeft || 0,
            )
          : remainingUsage.monthlyPromptsLeft || 0;

      return basePromptsLeft + (remainingUsage.extraPromptsInPackages || 0);
    } else {
      return remainingUsage.tokensLeft || 0;
    }
  }, [remainingUsage]);

  // Only render if there's a usage message and supabase is initialised
  if (!session) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        data-tooltip-id="see-usage"
        className="w-auto py-0 h-7 px-2 rounded-md transition-colors bg-secondary-n-950 flex items-center gap-2 shrink"
      >
        <CoinsIcon className="size-3 text-text-secondary" />
        <span className="text-xs text-secondary-n-50 whitespace-nowrap">
          {typeof totalLeft === "number"
            ? formatPromptCount(totalLeft)
            : totalLeft}
          {hasClaudeCodeAuth && (
            <>
              <span className="mx-0.5">+</span>
              <span>Unlimited Claude Code</span>
            </>
          )}
        </span>
      </Button>
      <Tooltip
        id="see-usage"
        className="opaque z-200"
        style={{
          backgroundColor: "black",
          color: "white",
          fontSize: "0.625rem",
          lineHeight: "0.5rem",
          fontWeight: 500,
        }}
        opacity={1}
        arrowColor="transparent"
        render={() => (
          <div className="flex flex-col gap-1 text-xs bg-black rounded-md pt-1.5">
            {hasClaudeCodeAuth ? (
              <div>
                <span>
                  <UsageText hasClaudeCodeAuth={hasClaudeCodeAuth} />
                </span>
                <span> + Unlimited Claude Code</span>
              </div>
            ) : (
              <UsageText hasClaudeCodeAuth={hasClaudeCodeAuth} />
            )}
          </div>
        )}
      />
    </>
  );
};

export default TopUsageIndicator;
